import {
    EventPostV4DTO,
    KilledEventPostV4DTO,
} from '@west-australian-newspapers/publication-types'
import { useState } from 'react'
import { dedupeEventPosts } from '.'
import { liveEventDebug } from '../helpers'
import { findIndex, getLast } from './post-helpers'

interface PostState {
    activePosts: EventPostV4DTO[]
    knownPosts: Array<EventPostV4DTO | KilledEventPostV4DTO>
    morePosts: {
        /** Newer posts */
        after: number
        /** Older posts */
        before: number
    }
}

export const usePostManager = (
    initialState: Array<EventPostV4DTO | KilledEventPostV4DTO>,
) => {
    const [postState, setPostState] = useState<PostState>({
        activePosts: dedupeEventPosts(initialState, []),
        knownPosts: initialState,
        morePosts: {
            after: 0,
            before: Math.max(initialState.length - 10, 0),
        },
    })

    const addPosts = (
        posts: Array<EventPostV4DTO | KilledEventPostV4DTO>,
        referencePostId: string,
        totals: {
            postsAbove: number
            postsBelow: number
        },
    ) => {
        if (posts.length === 0) {
            return
        }

        setPostState((prev) => {
            const activePosts = dedupeEventPosts(prev.activePosts, posts)
            const knownPosts = dedupeEventPosts(prev.knownPosts, posts)

            const referenceIndex = findIndex(activePosts, referencePostId)

            const postsAfter = totals.postsAbove - referenceIndex
            const postsBefore =
                totals.postsBelow - (activePosts.length - referenceIndex - 1)

            return {
                activePosts,
                knownPosts,
                morePosts: {
                    after: postsAfter,
                    before: postsBefore,
                },
            }
        })
    }

    const addPost = (
        post: EventPostV4DTO | KilledEventPostV4DTO,
        setActive = false,
    ) => {
        setPostState((prev) => {
            liveEventDebug('Adding post to known posts: %o', post)

            const knownPosts = dedupeEventPosts(prev.knownPosts, [post])
            liveEventDebug('Setting known posts: %o', knownPosts)

            const newPostIndex = findIndex(knownPosts, post.id)
            liveEventDebug(`New post's index: %s`, newPostIndex)

            const isDeleted = post.state === 'deleted'

            if (isDeleted) {
                liveEventDebug(`Received killed post, removing`)
            }

            const activePosts =
                setActive || isDeleted
                    ? dedupeEventPosts(prev.activePosts, [post])
                    : prev.activePosts

            const isExistingPost = findIndex(prev.knownPosts, post.id) >= 0
            const morePosts = prev.morePosts

            if (!isExistingPost && newestVisiblePost && oldestVisiblePost) {
                morePosts.after =
                    newPostIndex < findIndex(knownPosts, newestVisiblePost.id)
                        ? morePosts.after + 1
                        : morePosts.after
                morePosts.before =
                    newPostIndex > findIndex(knownPosts, oldestVisiblePost.id)
                        ? morePosts.before + 1
                        : morePosts.before
            }

            return {
                activePosts,
                knownPosts,
                morePosts,
            }
        })
    }

    const setActivePosts = (posts: EventPostV4DTO[]) => {
        liveEventDebug('Setting active posts: %o', posts)
        setPostState((prev) => ({ ...prev, activePosts: posts }))
    }

    const oldestVisiblePost: EventPostV4DTO | undefined = getLast(
        postState.activePosts,
    )
    const newestVisiblePost: EventPostV4DTO | undefined =
        postState.activePosts[0]

    const oldestKnownPost: EventPostV4DTO | KilledEventPostV4DTO | undefined =
        getLast(postState.knownPosts)
    const latestKnownPost: EventPostV4DTO | KilledEventPostV4DTO | undefined =
        postState.knownPosts[0]

    return {
        ...postState,
        addPost,
        addPosts,
        setActivePosts,
        oldestVisiblePost,
        newestVisiblePost,
        oldestKnownPost,
        latestKnownPost,
    }
}
